import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Loader2,
  Monitor,
  Smartphone,
  Mail,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  emailQueueAPI,
  type EmailTemplateType,
} from '../../lib/api/emailQueue';

// ---------------------------------------------------------------------------
// Category grouping
// ---------------------------------------------------------------------------

const CATEGORY_ORDER = [
  'Purchase Orders',
  'Quality',
  'Suppliers',
  'RFQ',
  'Users',
  'Documents',
  'Onboarding',
  'Call-Off',
  'System',
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EmailTemplatePreview() {
  const [grouped, setGrouped] = useState<Record<string, EmailTemplateType[]>>({});
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');

  // Fetch template types on mount
  useEffect(() => {
    emailQueueAPI.listTemplateTypes()
      .then((res) => {
        const data = res.data && typeof res.data === 'object' ? res.data : {};
        setGrouped(data as Record<string, EmailTemplateType[]>);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load template types';
        toast.error(msg);
      })
      .finally(() => setLoadingTypes(false));
  }, []);

  const fetchPreview = useCallback(async (type: string) => {
    setLoadingPreview(true);
    setPreviewHtml(null);
    try {
      const res = await emailQueueAPI.previewTemplate(type);
      setPreviewHtml(res.data.html);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load template preview';
      toast.error(msg);
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const handleSelectType = (type: string) => {
    setSelectedType(type);
    fetchPreview(type);
  };

  const hasTypes = Object.keys(grouped).length > 0;
  const iframeWidth = viewMode === 'desktop' ? 600 : 375;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Eye className="h-6 w-6" />
          Email Template Preview
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Preview branded email templates for each notification type
        </p>
      </div>

      <div className="flex gap-6" style={{ minHeight: '70vh' }}>
        {/* Left panel - Type selector */}
        <Card className="w-[280px] flex-shrink-0 overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Notification Types</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingTypes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !hasTypes ? (
              <div className="text-center py-12 px-4 text-sm text-muted-foreground">
                No template types available
              </div>
            ) : (
              <div className="overflow-y-auto max-h-[calc(70vh-80px)]">
                {CATEGORY_ORDER.filter(cat => grouped[cat]).map((category) => (
                  <div key={category}>
                    <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 border-y border-border">
                      {category}
                    </div>
                    {grouped[category].map((t) => (
                      <button
                        key={t.value}
                        onClick={() => handleSelectType(t.value)}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-border/50 ${
                          selectedType === t.value
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Mail className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
                          {t.label}
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
                {/* Render any categories not in CATEGORY_ORDER */}
                {Object.keys(grouped)
                  .filter(cat => !CATEGORY_ORDER.includes(cat))
                  .map((category) => (
                    <div key={category}>
                      <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50 border-y border-border">
                        {category}
                      </div>
                      {grouped[category].map((t) => (
                        <button
                          key={t.value}
                          onClick={() => handleSelectType(t.value)}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-border/50 ${
                            selectedType === t.value
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-foreground hover:bg-accent'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
                            {t.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right panel - Preview */}
        <div className="flex-1 flex flex-col">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              {selectedType
                ? `Previewing: ${Object.values(grouped).flat().find((t: EmailTemplateType) => t.value === selectedType)?.label ?? selectedType}`
                : 'Select a notification type to preview'}
            </div>
            <div className="flex items-center gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === 'desktop' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('desktop')}
              >
                <Monitor className="h-4 w-4 mr-1" />
                Desktop
              </Button>
              <Button
                variant={viewMode === 'mobile' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('mobile')}
              >
                <Smartphone className="h-4 w-4 mr-1" />
                Mobile
              </Button>
            </div>
          </div>

          {/* Preview area */}
          <Card className="flex-1 overflow-hidden">
            <CardContent className="p-6 flex items-start justify-center h-full overflow-auto bg-muted/30">
              {!selectedType && (
                <div className="flex flex-col items-center justify-center h-full text-center py-20">
                  <Mail className="h-12 w-12 text-muted-foreground/30 mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">Select a notification type</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Choose from the panel on the left to see a branded preview
                  </p>
                </div>
              )}

              {selectedType && loadingPreview && (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}

              {selectedType && !loadingPreview && previewHtml && (
                <div className="transition-all duration-300" style={{ width: iframeWidth, maxWidth: '100%' }}>
                  <iframe
                    srcDoc={previewHtml}
                    title="Email preview"
                    className="w-full border border-border rounded-lg bg-white shadow-sm"
                    style={{ width: iframeWidth, height: '600px', maxWidth: '100%' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              )}

              {selectedType && !loadingPreview && !previewHtml && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <AlertTriangleIcon className="h-10 w-10 text-amber-400 mb-2" />
                  <p className="text-sm font-medium text-muted-foreground">
                    Preview unavailable for this template
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline warning icon to avoid importing AlertTriangle at the top
 * (only used in rare error state).
 */
function AlertTriangleIcon({ className }: { className?: string }) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>;
}
